import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AOS from 'aos';
import 'aos/dist/aos.css';
import PageHeader from '../PageHeader';
import MapboxMap from './MapboxMap';

function KontakKami() {
    const [kantorData, setKantorData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        AOS.init({ duration: 700, once: true });
        const controller = new AbortController();

        fetch(`${process.env.REACT_APP_BACKEND_URL}/kantors`, { signal: controller.signal })
            .then((response) => response.json())
            .then((data) => setKantorData(Array.isArray(data) ? data : []))
            .catch((error) => {
                if (error.name !== 'AbortError') console.error('Error fetch kantor:', error);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, []);

    return (
        <>
            <Helmet><title>Kontak Kami | PT Len Telekomunikasi Indonesia</title></Helmet>
            <PageHeader title="Kontak Kami" />
            <main className="bg-slate-50 py-10 sm:py-14 lg:py-16" id="id-kontak-kami">
                <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <section className="mb-7 overflow-hidden rounded-[30px] bg-gradient-to-br from-slate-950 via-[#0a213a] to-[#07598e] p-7 text-white shadow-[0_22px_60px_rgba(15,23,42,0.16)] sm:p-9 lg:p-10">
                        <span className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-300">Hubungi Kami</span>
                        <div className="mt-3 grid gap-5 lg:grid-cols-[1fr_0.72fr] lg:items-end">
                            <h1 className="text-3xl font-bold leading-tight sm:text-4xl">Terhubung dengan PT Len Telekomunikasi Indonesia</h1>
                            <p className="text-sm leading-7 text-slate-300 sm:text-base">Temukan lokasi kantor atau kirim pesan melalui kanal kontak yang tersedia.</p>
                        </div>
                    </section>

                    <div className="grid gap-7 lg:grid-cols-[1.08fr_0.92fr]">
                        <div className="space-y-7">
                            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.07)]" data-aos="fade-up">
                                <div className="border-b border-slate-100 p-6 sm:p-7">
                                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Lokasi</span>
                                    <h2 className="mt-1 text-2xl font-bold text-slate-950">Peta Lokasi</h2>
                                </div>
                                <div className="bg-slate-900">
                                    <MapboxMap kantorData={kantorData} />
                                </div>
                            </section>

                            <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.07)] sm:p-7" data-aos="fade-up">
                                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Alamat</span>
                                <h2 className="mt-1 text-2xl font-bold text-slate-950">Kantor Kami</h2>
                                <div className="mt-5 space-y-3">
                                    {loading && Array.from({ length: 2 }).map((_, index) => <div key={index} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}
                                    {!loading && kantorData.length === 0 && <p className="rounded-2xl bg-slate-50 p-5 text-sm text-slate-500">Data kantor belum tersedia.</p>}
                                    {!loading && kantorData.map((kantor, index) => (
                                        <article key={kantor.id || index} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                            {kantor.nama_kantor && <h3 className="font-bold text-slate-950">{kantor.nama_kantor}</h3>}
                                            <p className="mt-2 text-sm leading-6 text-slate-600">{kantor.alamat}</p>
                                            <p className="text-sm text-slate-500">{kantor.kota}{kantor.kode_pos ? ` ${kantor.kode_pos}` : ''}</p>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href="https://www.google.com/maps/place/PT+Len+Telekomunikasi+Indonesia/@-6.2257701,106.8602768,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69f3fd8cd1f407:0x6eceab8064cbbc71!8m2!3d-6.2257701!4d106.8602768!16s%2Fg%2F11sz92mc01"
                                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
                                            >
                                                Buka Maps <span aria-hidden="true">&rarr;</span>
                                            </a>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        </div>

                        <section className="h-fit rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.07)] sm:p-8 lg:sticky lg:top-28" data-aos="fade-up">
                            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Pesan</span>
                            <h2 className="mt-1 text-2xl font-bold text-slate-950">Hubungi Kami</h2>
                            <p className="mt-2 text-sm leading-6 text-slate-500">Form akan membuka aplikasi email Anda dan mengirim ke marketing@len-telko.co.id.</p>
                            <form action="mailto:marketing@len-telko.co.id" method="POST" encType="text/plain" className="mt-7 space-y-5">
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-700">Nama Lengkap</span>
                                    <input name="Nama" type="text" required className="mt-2 block w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50" />
                                </label>
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-700">Nomor HP</span>
                                    <input name="Nomor_HP" type="tel" required className="mt-2 block w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50" />
                                </label>
                                <label className="block">
                                    <span className="text-sm font-semibold text-slate-700">Pesan Anda</span>
                                    <textarea name="Pesan" rows="6" required className="mt-2 block w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50" />
                                </label>
                                <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#005da2] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#064e83]">
                                    Kirim via Email <span aria-hidden="true">&rarr;</span>
                                </button>
                            </form>
                        </section>
                    </div>
                </div>
            </main>
        </>
    );
}

export default KontakKami;
